import * as React from 'react';
import { Res, OnlineReservationInListDTO, Permission } from 'core/api';
import { startOfDay } from 'date-fns';
import * as UI from 'core/ui';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { Preconditions } from 'core/infrastructure-next';
import * as Images from 'pos/Images';
import { ReservationList } from './ReservationList';
import { Command } from 'pos/common';
import { DateUtils } from 'core/utils';
import getAvailableReservations from './getAvailableReservations';
import { Flex, Cols, Col } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';
import { legacyControlsFor } from 'core/ui-next/controls';

type Props = {
	initialReservations: OnlineReservationInListDTO[];
	initialDate: Date;
} & Modal.Actions<OnlineReservationInListDTO>;

type State = {
	reservations: OnlineReservationInListDTO[];
	date: Date;
	searchText: string;
	sortField: keyof OnlineReservationInListDTO;
	sortDirection: 'asc' | 'desc';
	selected: Nullable<OnlineReservationInListDTO>;
};

let lastSelectedDate: Nullable<Date> = null;
class SelectReservationScreen extends PosScreenClassComponent<Props, State> {

	private form = React.createRef<UI.Form<State>>();

	createInitialState(props: Props): Nullable<State> {
		return {
			reservations: [...props.initialReservations],
			date: props.initialDate,
			searchText: '',
			sortField: 'StartTime',
			sortDirection: 'asc',
			selected: null
		};
	}

	onComponentDidMount(): void { }
	onComponentWillUnmount(): void {
		lastSelectedDate = this.state.date;
	}

	protected getScreenId(): string {
		return 'SELECT_RESERVATION';
	}

	protected getTitle(): string {
		return Res.Web.SELECT_ONLINE_RESERVATION();
	}

	protected getImage(): string | SvgIcon {
		return Images.OnlineReservation;
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return this.okCancelButtons({
			onOK: async () => {
				if (this.state.selected === null) {
					await Message.show(Res.Msg.MSG_NOT_SELECTED_RESERVATION());
					return;
				}

				this.close(this.state.selected);
			}
		});
	}

	private sort = (sortField: keyof OnlineReservationInListDTO) => {
		const sortDirection = this.state.sortField === sortField && this.state.sortDirection === 'asc' ? 'desc' : 'asc';
		this.setState({ sortField, sortDirection });
	};

	private getFilteredOrders(): OnlineReservationInListDTO[] {
		const reservations = this.state.reservations.filter(x => {
			return !this.state.searchText || [
				DateUtils.formatDate(x.StartTime, 'HH:mm'), x.Companions.toString(), x.Customer, x.Location, x.Notes
			].some(field => field.toLowerCase().includes(this.state.searchText.toLowerCase()));
		});

		return this.state.sortField ? reservations.sortBy(x => (x as any)[this.state.sortField], this.state.sortDirection) : reservations;
	}

	private onClearSearchText = () => {
		this.setState({ searchText: '' }, async () => await this.form.current!.remountAll());
	};


	protected getContent(): React.ReactNode {

		const { Form, TextBox } = UI.controlsFor<State>();
		const { DatePicker } = legacyControlsFor<State>();

		const onSelectReservation = (reservation: OnlineReservationInListDTO) => this.setState({ selected: reservation });

		const onValueChanged = async (date: Nullable<Date>) => {
			const reservations = await getAvailableReservations(date!, '');
			this.setState({ date: date!, reservations }, async () => await this.form.current!.remountAll());
		};


		return <Flex.Vertical>
			<Flex.Item>
				<Form ref={this.form} className='m-t-sm' model={this.state}>
					<div className='hidden-xs hidden-sm'>
						<Cols style={{ justifyContent: 'center' }}>
							<Col className='m-t-xs m-r-xs m-l-xs text-16'>
								{Res.Web.SELECT_ONLINE_RESERVATION_DATE_LBL()}
							</Col>
							<Col>
								<DatePicker className='text-center' linkTo={x => x.date} onValueChanged={onValueChanged} required tabIndex={-1} />
							</Col>
							<Col width='*' className='m-l-lg'>
								<TextBox linkTo={x => x.searchText} placeholder={Res.Web.SELECT_ONLINE_RESERVATION_SEARCH_TEXT_PLACEHOLDER()} onValueChange={searchText => this.setState({ searchText })}>
									<UI.TextBoxButton iconClassName='fa fa-times text-info' onClick={this.onClearSearchText} />
								</TextBox>
							</Col>
							<Col className='m-l-md'>
								<SortMenu sortField={this.state.sortField} sortDirection={this.state.sortDirection} sort={this.sort} />
							</Col>
						</Cols>
					</div>
					<div className='hidden-md hidden-lg'>
						<Cols style={{ justifyContent: 'center' }}>
							<Col className='m-t-xs m-r-xs m-l-xs text-16'>
								{Res.Web.SELECT_ONLINE_RESERVATION_DATE_LBL()}
							</Col>
							<Col width='*'>
								<DatePicker className='text-center' linkTo={x => x.date} onValueChanged={onValueChanged} required tabIndex={-1} />
							</Col>
						</Cols>
						<Cols style={{ justifyContent: 'center' }}>
							<Col width='*' className='m-l-xs'>
								<TextBox linkTo={x => x.searchText} placeholder={Res.Web.SELECT_ONLINE_RESERVATION_SEARCH_TEXT_PLACEHOLDER()} onValueChange={searchText => this.setState({ searchText })}>
									<UI.TextBoxButton iconClassName='fa fa-times text-info' onClick={this.onClearSearchText} />
								</TextBox>
							</Col>
							<Col className='m-l-sm'>
								<SortMenu sortField={this.state.sortField} sortDirection={this.state.sortDirection} sort={this.sort} />
							</Col>
						</Cols>
					</div>
				</Form>
			</Flex.Item>
			<Flex.Item size='*'>
				<ReservationList
					headerText={Res.Web.SELECT_ONLINE_RESERVATION_DATE_RESERVATIONS_LBL()}
					items={this.getFilteredOrders()}
					selected={this.state.selected}
					onItemSelected={reservation => onSelectReservation(reservation)}
				/>
			</Flex.Item>
		</Flex.Vertical>;

	}
}

// Este control también está en showTakeOutOrders. A lo mejor se podría reutilizar haciéndolo genérico y exponiendo
// un método constructor que cierre el tipo como los controles de WebAdmin
const SortMenu = ({ sortField, sortDirection, sort }: { sortField: keyof OnlineReservationInListDTO, sortDirection: 'asc' | 'desc', sort: (sortField: keyof OnlineReservationInListDTO) => void }) => {

	const item = (key: number, text: string, sortField: keyof OnlineReservationInListDTO) => {
		return <li key={key} className='p-xs' onClick={() => sort(sortField)}>{text}</li>;
	};

	const sortFields: [keyof OnlineReservationInListDTO, string][] = [
		['StartTime', Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_START_TIME_LBL()],
		['Companions', Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_COMPANIONS_LBL()],
		['Customer', Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_CUSTOMER_LBL()],
		['Location', Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_LOCATION_LBL()],
		['Notes', Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_NOTES_LBL()]
	];

	return <div className='btn-group dropdown'>
		<a data-toggle='dropdown' className='btn btn-default dropdown-toggle '>
			{sortFields.find(([sField]) => sField === sortField)![1]}
			<i className={`text-info fa ${sortDirection === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc'} m-l-xs`}></i>
		</a>
		<ul className='dropdown-menu dropdown-menu-right'>
			{sortFields.map(([sortField, label], idx) => item(idx, label, sortField))}
		</ul>
	</div>;
};


declare global {
	interface CommandType {
		selectReservation: () => Promise<Result<OnlineReservationInListDTO>>
	}
}


async function selectReservation(): Promise<Result<OnlineReservationInListDTO>> {
	const initialDate = startOfDay(lastSelectedDate || new Date());
	const initialReservations = await getAvailableReservations(initialDate, '');
	return Modal.show(SelectReservationScreen, { initialDate, initialReservations });
}

Command.register('selectReservation', Preconditions.and(Preconditions.ensure(Permission.OnlineReservation_Attend), Preconditions.ensureIsNotLite), selectReservation);

